import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { FONT, COLOURS } from '../constants/theme';
import { COUPLE }        from '../constants/weddingData';
import { SCREENS }       from '../App';
import StatusBar         from '../components/ui/StatusBar';
import Dialog            from '../components/ui/Dialog';
import GuestCrowd        from '../components/pixel/GuestCrowd';
import MatchaDog         from '../components/pixel/MatchaDog';
import CanadianFlag      from '../components/pixel/CanadianFlag';

/** X-coordinate the guest crowd walks to when boarding the Toronto boat. */
const TORONTO_BOARD_X = 58;
/** X-coordinate the guest crowd walks to when boarding the Japan boat. */
const JAPAN_BOARD_X   = 272;
/** Starting X for the guest crowd (centred on the dock). */
const START_X         = 173;
/** Walk speed in SVG units per interval tick. */
const WALK_SPEED      = 2.5;
/** Interval tick in milliseconds. */
const TICK_MS         = 25;
/** Pause before triggering the screen transition after boarding (ms). */
const BOARD_DELAY_MS  = 500;

/**
 * DockScene — the world-select screen.
 *
 * Renders a golden-hour lakeside scene with:
 *   - A Toronto-bound boat (left) with Canadian flag
 *   - A Japan-bound boat (right) with Japanese flag + pagoda cabin
 *   - A diverse pixel-art guest crowd that walks toward the chosen boat
 *   - Matcha the dog on the dock
 *   - The Toronto city skyline with CN Tower (right horizon)
 *
 * Guest animation uses a setInterval inside a useEffect with proper cleanup
 * to avoid memory leaks or updates on unmounted components.
 *
 * @param {object}   props
 * @param {function} props.onBoard - Callback receiving the destination slug
 *                                   ('toronto' | 'japan').
 */
function DockScene({ onBoard }) {
  // Guest crowd horizontal position (SVG units)
  const [guestX,  setGuestX]  = useState(START_X);
  const [walking, setWalking] = useState(false);

  const timerRef = useRef(null);

  /** Clear the walk interval on unmount to prevent memory leaks. */
  useEffect(() => () => clearInterval(timerRef.current), []);

  /**
   * Animate the crowd walking to `destX`, then fire `onBoard(boat)`.
   * Uses functional setState to avoid stale closure over `guestX`.
   *
   * @param {number} destX - Target SVG x-coordinate.
   * @param {string} boat  - Destination slug passed to `onBoard`.
   */
  const walkTo = (destX, boat) => {
    setWalking(true);
    clearInterval(timerRef.current);

    const step = destX < guestX ? -WALK_SPEED : WALK_SPEED;

    timerRef.current = setInterval(() => {
      setGuestX((prev) => {
        const next = prev + step;
        const arrived = step < 0 ? next <= destX : next >= destX;

        if (arrived) {
          clearInterval(timerRef.current);
          setWalking(false);
          setTimeout(() => onBoard(boat), BOARD_DELAY_MS);
          return destX;
        }
        return next;
      });
    }, TICK_MS);
  };

  /* ── Shared SVG shorthand colour aliases ── */
  const C = COLOURS;

  return (
    <div
      className="fadeIn"
      style={{ fontFamily: FONT, background: C.parchment, minHeight: '100vh' }}
    >
      <StatusBar left="✿ LOVI & MARKO" mid="CHOOSE YOUR VOYAGE" right="♥♥♥♥♥" />

      {/* ════════════════════════════════════════════════════
          MAIN SCENE — golden-hour lakeside dock
          SVG coordinate space: 380 × 320 units
          ════════════════════════════════════════════════════ */}
      <svg
        width="100%"
        viewBox="0 0 380 320"
        style={{ display: 'block' }}
        role="img"
        aria-label="Lakeside dock with two boats — Toronto (left) and Japan (right)"
      >
        {/* ── SKY — warm golden hour gradient approximated with rects ── */}
        <rect x="0" y="0"   width="380" height="320" fill="#f5c890" />
        <rect x="0" y="0"   width="380" height="58"  fill="#e8904a" />
        <rect x="0" y="58"  width="380" height="78"  fill="#f5aa68" />
        <rect x="0" y="136" width="380" height="56"  fill="#f0d8b0" />

        {/* ── SUN ── */}
        <rect x="162" y="20" width="56" height="56" fill="#FFD060" />
        <rect x="156" y="30" width="6"  height="36" fill="#FFD060" />
        <rect x="218" y="30" width="6"  height="36" fill="#FFD060" />
        <rect x="168" y="14" width="44" height="6"  fill="#FFD060" />
        <rect x="168" y="76" width="44" height="6"  fill="#FFD060" />
        {/* Subtle sun-glow band across horizon */}
        <rect x="0"   y="58" width="380" height="16" fill="#FFD060" opacity="0.07" />

        {/* ── CLOUDS ── */}
        {[[12,22,62,14],[6,29,74,14],[18,18,40,10],[262,16,52,12],[256,23,64,14]].map(
          ([cx,cy,cw,ch], i) => (
            <rect key={i} x={cx} y={cy} width={cw} height={ch} fill="#fff0d8" opacity="0.85" />
          )
        )}

        {/* ── TORONTO CITY SKYLINE — right horizon ── */}
        {/* Background buildings */}
        <rect x="236" y="100" width="18" height="72" fill="#d4bba0" opacity="0.6" />
        <rect x="256" y="112" width="14" height="60" fill="#c8b090" opacity="0.6" />
        <rect x="272" y="96"  width="20" height="76" fill="#d0b898" opacity="0.6" />
        <rect x="294" y="108" width="16" height="64" fill="#c8b090" opacity="0.6" />
        <rect x="312" y="88"  width="22" height="84" fill="#d4bba0" opacity="0.65"/>
        <rect x="336" y="102" width="14" height="70" fill="#c8b090" opacity="0.6" />
        <rect x="352" y="92"  width="18" height="80" fill="#d0b898" opacity="0.65"/>

        {/* CN Tower — distinctive silhouette */}
        <rect x="244" y="142" width="6"  height="30" fill="#b0a888" />  {/* base leg L */}
        <rect x="264" y="142" width="6"  height="30" fill="#b0a888" />  {/* base leg R */}
        <rect x="251" y="80"  width="8"  height="92" fill="#c0b898" />  {/* main shaft */}
        <rect x="252" y="82"  width="6"  height="88" fill="#d0c8a8" />  {/* shaft highlight */}
        <rect x="246" y="118" width="18" height="14" fill="#c0b090" />  {/* pod outer */}
        <rect x="247" y="120" width="16" height="10" fill="#d4c8a0" />  {/* pod mid */}
        <rect x="248" y="121" width="14" height="8"  fill="#e0d4b0" opacity="0.8" />  {/* pod inner */}
        <rect x="249" y="122" width="3"  height="4"  fill="#a8d8f0" opacity="0.7" />  {/* window 1 */}
        <rect x="254" y="122" width="3"  height="4"  fill="#a8d8f0" opacity="0.7" />  {/* window 2 */}
        <rect x="259" y="122" width="3"  height="4"  fill="#a8d8f0" opacity="0.7" />  {/* window 3 */}
        <rect x="254" y="60"  width="2"  height="22" fill="#a8a088" />  {/* antenna lower */}
        <rect x="254" y="56"  width="2"  height="6"  fill="#888070" />  {/* antenna tip */}
        <rect x="244" y="170" width="22" height="4"  fill="#a8a080" />  {/* base structure */}
        <rect x="246" y="172" width="18" height="2"  fill="#909070" />

        {/* Lit windows on far buildings */}
        {[[238,108],[248,108],[238,122],[248,122],
          [274,104],[286,104],[274,118],[286,118],
          [314,96], [326,96], [314,110],[326,110]
        ].map(([wx,wy], i) => (
          <rect key={i} x={wx} y={wy} width="5" height="5"
            fill={i % 3 === 0 ? '#fffacc' : '#a8d8f0'}
            opacity={i % 3 === 0 ? 0.5 : 0.4}
          />
        ))}

        {/* ── MAPLE TREES — left bank ── */}
        {[
          [0,150,12,28,'#c85a28'],[5,118,40,44,'#d85a20'],[8,110,32,38,'#c84a18'],
          [12,102,24,34,'#e87030'],[2,146,42,20,'#d86030'],
          [50,152,10,26,'#8a5a28'],[36,120,36,40,'#c84a18'],
          [40,112,28,36,'#e07028'],[34,148,38,18,'#d86030'],
        ].map(([x,y,w,h,col], i) => (
          <rect key={i} x={x} y={y} width={w} height={h} fill={col} opacity="0.92" />
        ))}

        {/* Right-bank accent trees (fewer — CN Tower is the focal point) */}
        {[
          [356,150,12,28,'#c85a28'],[350,118,22,38,'#d85a20'],[354,112,18,32,'#e07028'],
        ].map(([x,y,w,h,col], i) => (
          <rect key={i} x={x} y={y} width={w} height={h} fill={col} opacity="0.92" />
        ))}

        {/* ── FALLING MAPLE LEAVES ── */}
        {[35,78,118,172,242,293,336].map((lx, i) => (
          <rect
            key={i}
            x={lx} y={84 + (i % 4) * 16}
            width="7" height="6"
            fill={['#c8481a','#d85a20','#e87030','#b83a10'][i % 4]}
            opacity="0.85"
            style={{ animation: `leaf ${4.5 + i * 0.5}s ${i * 0.6}s ease-in infinite` }}
          />
        ))}

        {/* ── WATER / LAKE ── */}
        <rect x="0" y="178" width="380" height="142" fill="#6aa8c8" />
        {/* Animated shimmer lines */}
        {[182,194,208,222,238].map((wy, i) => (
          <rect
            key={i} x={14 + i * 8} y={wy}
            width={54 + i * 9} height="2"
            fill="#9ad8f0" opacity="0.3"
            style={{ animation: `shim ${2 + i * 0.3}s ${i * 0.2}s ease-in-out infinite` }}
          />
        ))}
        {/* Sun reflection on water */}
        <rect x="148" y="179" width="84" height="96" fill="#FFD060" opacity="0.07" />

        {/* ── GROUND / BANK ── */}
        <rect x="0" y="166" width="380" height="16" fill="#a8c870" />
        <rect x="0" y="172" width="380" height="8"  fill="#88a850" />
        <rect x="0" y="178" width="380" height="4"  fill="#5a8030" />
        {/* Stone path leading to dock */}
        {[160,176,192,208].map((px) => (
          <rect key={px} x={px} y="168" width="12" height="5" fill="#b09870" opacity="0.5" />
        ))}

        {/* ── DOCK ── */}
        <rect x="152" y="175" width="76" height="52" fill="#c4a068" />
        {/* Plank lines */}
        {[181,189,197,205,213,221].map((py) => (
          <rect key={py} x="152" y={py} width="76" height="2" fill="#b09050" opacity="0.5" />
        ))}
        {/* Dock framing */}
        <rect x="149" y="174" width="82" height="5"  fill="#8a6830" />
        <rect x="149" y="223" width="82" height="5"  fill="#8a6830" />
        <rect x="149" y="174" width="5"  height="54" fill="#8a6830" />
        <rect x="226" y="174" width="5"  height="54" fill="#8a6830" />
        {/* Dock posts */}
        {[152,170,208,222].map((px) => (
          <rect key={px} x={px} y="226" width="8" height="20" fill="#7a5820" />
        ))}
        {/* Mooring ropes */}
        <rect x="156" y="227" width="18" height="2" fill="#a08040" opacity="0.6" />
        <rect x="210" y="227" width="18" height="2" fill="#a08040" opacity="0.6" />
        {/* Dock sign post */}
        <rect x="182" y="159" width="8"  height="18" fill="#8a6830" />
        <rect x="168" y="155" width="36" height="16" fill="#e8d0a0" />
        <rect x="168" y="155" width="36" height="16" fill="none" stroke="#8a6830" strokeWidth="2" />
        <text x="186" y="166" textAnchor="middle" fontFamily={FONT} fontSize="5" fill="#6b3820">
          DOCK
        </text>

        {/* ════════════════════════════════════════════════
            TORONTO BOAT (left) — Canadian pleasure cruiser
            transform-origin matches the boat's visual centre
            ════════════════════════════════════════════════ */}
        <g className="boat-l">
          {/* Hull */}
          <rect x="6"   y="202" width="116" height="32" fill="#c85a28" />
          <rect x="2"   y="198" width="124" height="8"  fill="#d86a38" />
          <rect x="-2"  y="194" width="132" height="6"  fill="#e87848" />
          <rect x="6"   y="210" width="116" height="4"  fill="#b84a18" />
          <polygon points="-2,194 6,202 -2,234" fill="#c85a28" />
          {/* Deck */}
          <rect x="6"   y="186" width="116" height="10" fill="#e8d0a0" />
          {/* Cabin */}
          <rect x="24"  y="160" width="66"  height="28" fill="#f0e0b8" />
          <polygon points="16,162 57,140 98,162"  fill="#8a4a28" />
          <polygon points="18,162 57,142 96,162"  fill="#a05a38" />
          <rect x="16"  y="160" width="84"  height="5"  fill="#6b3820" />
          {/* Cabin windows */}
          {[30, 62].map((wx) => (
            <g key={wx}>
              <rect x={wx}    y="166" width="14" height="12" fill="#a8d8f0" />
              <rect x={wx}    y="166" width="14" height="3"  fill="#8a6830" />
              <rect x={wx}    y="166" width="3"  height="12" fill="#8a6830" />
              <rect x={wx+11} y="166" width="3"  height="12" fill="#8a6830" />
              <rect x={wx+5}  y="166" width="2"  height="12" fill="#d0c090" opacity="0.5" />
              <rect x={wx}    y="172" width="14" height="2"  fill="#d0c090" opacity="0.5" />
              <rect x={wx+3}  y="169" width="5"  height="5"  fill="#fffacc" opacity="0.6" />
            </g>
          ))}
          {/* Mast */}
          <rect x="52" y="128" width="6" height="36" fill="#8a6830" />
          {/* Canadian flag — reusable component */}
          <g className="flag-anim" transform="translate(58,130)">
            <CanadianFlag x={0} y={0} w={28} h={18} />
          </g>
          {/* Boat name plate */}
          <rect x="14"  y="174" width="88" height="14" fill="#a84020" />
          <rect x="14"  y="174" width="88" height="14" fill="none" stroke="#FFD060" strokeWidth="2" />
          <text x="58" y="184" textAnchor="middle" fontFamily={FONT} fontSize="5.5" fill="#FFD060">
            🍁 TORONTO
          </text>
          {/* Wake */}
          <rect x="-4" y="230" width="148" height="4" fill="#9ad8f0" opacity="0.45" />
        </g>

        {/* ════════════════════════════════════════════════
            JAPAN BOAT (right) — pagoda-cabin ryokan ferry
            ════════════════════════════════════════════════ */}
        <g className="boat-r">
          {/* Hull */}
          <rect x="260" y="202" width="116" height="32" fill="#8a5a28" />
          <rect x="256" y="198" width="124" height="8"  fill="#9a6a38" />
          <rect x="252" y="194" width="132" height="6"  fill="#a87848" />
          <rect x="260" y="210" width="116" height="4"  fill="#7a4a18" />
          <polygon points="252,194 260,202 252,234" fill="#8a5a28" />
          {/* Deck */}
          <rect x="260" y="186" width="116" height="10" fill="#e8d0a0" />
          {/* Pagoda cabin */}
          <rect x="278" y="162" width="64"  height="26" fill="#f0e0b8" />
          <polygon points="270,164 310,143 350,164" fill="#8a4a28" />
          <polygon points="272,164 310,145 348,164" fill="#a05a38" />
          <rect x="270" y="162" width="80"  height="5"  fill="#6b3820" />
          {/* Shoji windows */}
          {[284, 320].map((wx) => (
            <g key={wx}>
              <rect x={wx}    y="168" width="16" height="14" fill="#fff8e8" />
              <rect x={wx}    y="168" width="16" height="3"  fill="#8a5a28" />
              <rect x={wx}    y="168" width="3"  height="14" fill="#8a5a28" />
              <rect x={wx+13} y="168" width="3"  height="14" fill="#8a5a28" />
              <rect x={wx+6}  y="168" width="2"  height="14" fill="#d4c098" opacity="0.5" />
              <rect x={wx}    y="175" width="16" height="2"  fill="#d4c098" opacity="0.5" />
              <rect x={wx+3}  y="171" width="5"  height="5"  fill="#ffe0a0" opacity="0.7" />
            </g>
          ))}
          {/* Hanging lantern */}
          <rect x="308" y="162" width="6"  height="8"  fill="#fff8e8" />
          <rect x="306" y="160" width="10" height="4"  fill="#c9843c" />
          <rect x="306" y="170" width="10" height="3"  fill="#c9843c" />
          <rect x="303" y="158" width="16" height="20" fill="#ffd080" opacity="0.15" />
          {/* Mast */}
          <rect x="310" y="128" width="6"  height="38" fill="#7a5820" />
          {/* Japanese flag — Hinomaru */}
          <g className="flag-anim">
            <rect x="316" y="130" width="24" height="16" fill="#ffffff" />
            <rect x="316" y="130" width="24" height="16" fill="none" stroke="#dddddd" strokeWidth="1" />
            <circle cx="328" cy="138" r="4" fill="#cc2222" />
          </g>
          {/* Boat name plate */}
          <rect x="268" y="174" width="84" height="14" fill="#6b3820" />
          <rect x="268" y="174" width="84" height="14" fill="none" stroke="#c9843c" strokeWidth="2" />
          <text x="310" y="184" textAnchor="middle" fontFamily={FONT} fontSize="5.5" fill="#ffd080">
            ⛩ JAPAN
          </text>
          {/* Wake */}
          <rect x="248" y="230" width="148" height="4" fill="#9ad8f0" opacity="0.45" />
        </g>

        {/* ── GUEST CROWD — animated walk ── */}
        <GuestCrowd baseX={guestX} walking={walking} />

        {/* ── MATCHA on the dock ── */}
        <MatchaDog x={184} y={179} />

        {/* ── SPARKLE / FAIRY-LIGHT effects ── */}
        {[[110,184,0],[274,188,0.5],[152,180,0.3],[234,177,0.8]].map(([sx,sy,sd], i) => (
          <g key={i} className="sp" style={{ animationDelay: `${sd}s` }}>
            <rect x={sx}   y={sy}   width="4" height="4" fill="#ffe090" opacity="0.9" />
            <rect x={sx-2} y={sy+2} width="8" height="2" fill="#ffe090" opacity="0.4" />
            <rect x={sx+1} y={sy-2} width="2" height="8" fill="#ffe090" opacity="0.4" />
          </g>
        ))}

        {/* ── TITLE BANNER — floated above the scene ── */}
        <rect x="88"  y="4"  width="204" height="76" fill="#fff8f0" opacity="0.97" />
        <rect x="88"  y="4"  width="204" height="76" fill="none" stroke="#c9843c" strokeWidth="3" />
        {/* Corner accents */}
        {[[86,2],[284,2],[86,72],[284,72]].map(([cx,cy], i) => (
          <rect key={i} x={cx} y={cy} width="10" height="10" fill="#c9843c" />
        ))}
        {[[84,0],[286,0],[84,76],[286,76]].map(([cx,cy], i) => (
          <rect key={i} x={cx} y={cy} width="5" height="5" fill="#b85c38" />
        ))}
        <text x="190" y="22"  textAnchor="middle" fontFamily={FONT} fontSize="5"  fill="#8a6848">
          ✿ The Wedding of ✿
        </text>
        <text x="190" y="42"  textAnchor="middle" fontFamily={FONT} fontSize="14" fill="#b85c38">
          {COUPLE.bride}
        </text>
        <text x="190" y="58"  textAnchor="middle" fontFamily={FONT} fontSize="14" fill="#b85c38">
          &amp; {COUPLE.groom}
        </text>
        <text x="190" y="72"  textAnchor="middle" fontFamily={FONT} fontSize="5"  fill="#c9843c">
          ↓ Choose your voyage ↓
        </text>

        {/* Boat destination labels below the waterline */}
        <text x="64"  y="260" textAnchor="middle" fontFamily={FONT} fontSize="5.5" fill="#6b3820">Canada</text>
        <text x="64"  y="271" textAnchor="middle" fontFamily={FONT} fontSize="4.5" fill="#8a6848">Ceremony</text>
        <text x="318" y="260" textAnchor="middle" fontFamily={FONT} fontSize="5.5" fill="#6b3820">Osaka</text>
        <text x="318" y="271" textAnchor="middle" fontFamily={FONT} fontSize="4.5" fill="#8a6848">Ceremony</text>
      </svg>

      {/* ── BOARD BUTTONS ── */}
      <div
        style={{
          background:   C.parchment,
          padding:      '10px 12px',
          borderTop:    `3px solid ${C.borderGold}`,
          borderBottom: `3px solid ${C.borderGold}`,
        }}
      >
        <div
          style={{
            fontFamily:    FONT,
            fontSize:      7,
            color:         C.textMid,
            textAlign:     'center',
            marginBottom:  9,
          }}
        >
          ✿ Walk to the dock — choose your celebration ✿
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <button
            type="button"
            aria-label="Board the Toronto boat — Canada ceremony"
            onClick={() => walkTo(TORONTO_BOARD_X, SCREENS.TORONTO)}
            style={{
              fontFamily:  FONT,
              fontSize:    6,
              color:       C.textInverted,
              background:  C.torontoRed,
              border:      '3px solid #8a3a18',
              padding:     '10px 6px',
              cursor:      'pointer',
              boxShadow:   '3px 3px 0 #5a2010',
              lineHeight:  1.9,
            }}
          >
            🍁 BOARD
            <br />TORONTO BOAT
            <br />
            <span style={{ fontSize: 5, color: '#f5dbb8' }}>Canada Ceremony</span>
          </button>

          <button
            type="button"
            aria-label="Board the Japan boat — Osaka ceremony"
            onClick={() => walkTo(JAPAN_BOARD_X, SCREENS.JAPAN)}
            style={{
              fontFamily:  FONT,
              fontSize:    6,
              color:       C.textInverted,
              background:  C.japanGold,
              border:      '3px solid #8a5a18',
              padding:     '10px 6px',
              cursor:      'pointer',
              boxShadow:   '3px 3px 0 #5a3808',
              lineHeight:  1.9,
            }}
          >
            ⛩ BOARD
            <br />JAPAN BOAT
            <br />
            <span style={{ fontSize: 5, color: C.textInverted }}>Osaka Ceremony</span>
          </button>
        </div>
      </div>

      {/* Matcha dock dialog */}
      <div style={{ padding: '10px 12px' }}>
        <Dialog name="🐾 MATCHA [DOCK]">
          &quot;I can only come to one of these — I get seasick on long-haul flights.
          See you in Toronto! 🍁&quot;
          <span className="cursor">█</span>
        </Dialog>
      </div>
    </div>
  );
}

DockScene.propTypes = {
  onBoard: PropTypes.func.isRequired,
};

export default DockScene;
